import logger from '@tcp/core/src/utils/loggerInstance';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';
import { getAPIConfig, isServer } from '@tcp/core/src/utils';

const httpContext = isServer() ? require('express-http-context') : '';

let labels = {};
if (typeof window !== 'undefined') {
  labels = window?.labelsData || {};
}

/**
 * Retrieve nested values from an object based on a dot-separated key string.
 * @param {string} keys - Dot-separated key string representing nested keys.
 * @param {object} hardcodedLabelObj - Optional. Object containing the nested values.
 * @returns {any} - Returns the value of the nested key or relevant result based on the key length.
 */
const getLabelByNestedKeys = (keys, hardcodedLabelObj) => {
  try {
    if (isServer()) {
      const labelsData = httpContext.get('labelsData') || '{}';
      labels = (labelsData && JSON.parse(labelsData)) || {};
    }

    // Get the initial object to search for nested keys
    const initialValue = hardcodedLabelObj || labels;
    // Retrieve the value of the nested key using lodash's get method
    const result = get(initialValue, keys);

    // Check if the length of keys is 3
    if (typeof keys === 'string' && keys?.split('.').length === 3) {
      // If length is 3, return the value found or the key name if not found
      return result !== undefined ? result : keys?.split('.').pop() || null;
    }

    // For keys with length <= 2, handle object or empty object based on the result
    // eslint-disable-next-line no-nested-ternary
    return isObject(result) && !isArray(result)
      ? cloneDeep(result)
      : result !== undefined
      ? result
      : {};
  } catch (error) {
    // Log any errors that occur during the process
    logger.error(`Error in getLabelByNestedKeys: ${error}`);
    // Return the initial keys string in case of an error
    return keys;
  }
};

/**
 * Sets the labels data by merging it with the existing labels object.
 * @param {object} data - The new labels data to be merged.
 * @returns {object|null} - The updated labels object, or null if an error occurs.
 */
const setLabelsData = (data) => {
  try {
    if (isServer()) {
      const lbl = { ...labels, ...data };
      httpContext.set('labelsData', JSON.stringify(lbl));
    }
    // Merge the new labels data with the existing labels object
    labels = { ...labels, ...data };
  } catch (error) {
    // Log any errors that occur and return null as a fallback
    logger.error(`Error in setLabelsData: ${error}`);
  }
};

/**
 * Retrieves all labels from the labels object.
 * @returns {object|null} - The labels object, or null if an error occurs.
 */
const getAllLabels = () => {
  try {
    // Return the labels object
    if (isServer()) {
      const labelsData = httpContext.get('labelsData') || '{}';
      return JSON.parse(labelsData);
    }
    return labels;
  } catch (error) {
    // Log any errors that occur and return null as a fallback
    logger.error(`Error in getAllLabels: ${error}`);
    return null;
  }
};

/**
 * Retrieves the dynamic label values for a given label key.
 * @param {string} labelKey - The key of the label to retrieve.
 * @returns {string|object} - The retrieved label value, which can be either a string or
 * an object depending on whether the label key corresponds to a single label or
 * a nested object of labels.
 */
const getDynamicLabelValues = (labelKey) => {
  try {
    // Retrieve the label value using the getLabelByNestedKeys method
    return getLabelByNestedKeys(labelKey);
  } catch (error) {
    // Log any errors that occur and return the label key or default value {} as a fallback
    logger.error(`Error in getDynamicLabelValues: ${error}`);
    return labelKey;
  }
};

/**
 * Retrieves a label from either the hardcoded label object or the translation API.
 * If the locale is not 'en_us', it retrieves the label from the translation API.
 * If the locale is 'en_us', it retrieves the label from the hardcoded label object.
 * @param {string} labelKey - The key of the label to retrieve.
 * @param {object} hardcodedLabelObj - The object containing hardcoded labels used to
 * retrieve labels when the locale is 'en_us'.
 * @returns {string|object} - The retrieved label, which can be either a string or
 * an object depending on whether the label key corresponds to a single label or
 * a nested object of labels.
 */
const getLabelFromCopyOrFromTranslation = (labelKey, hardcodedLabelObj) => {
  try {
    // Get the API configuration and locale
    const apiConfig = getAPIConfig();
    const locale =
      apiConfig?.locale || (typeof window !== 'undefined' && window?.locale) || 'en_us';

    // If the locale is not 'en_us and en_ca', retrieve the label from the translation API
    const lowerLocale = locale?.toLowerCase();
    if (lowerLocale !== 'en_us' && lowerLocale !== 'en_ca') {
      return getLabelByNestedKeys(labelKey);
    }

    // If the locale is 'en_us', retrieve the label from the hardcoded label object
    return getLabelByNestedKeys(labelKey, hardcodedLabelObj);
  } catch (error) {
    // Log any errors that occur and return the label key or default value {} as a fallback
    logger.error(`Error in getLabelFromCopyOrFromTranslation: ${error}`);
    return labelKey;
  }
};

const renderLabel = (label, fallback) => (label !== undefined ? label : fallback);

/**  Unlike renderLabel, this function takes labels object and key to return the value if it matches or its key if doesn't.
 * This helps in removing multiple duplicate keys.
 * Use getLabelValue instead to extract key from nested object.
 */
const getLabelWithDefaultValue = (labelsObj, key) => {
  if (isObject(labelsObj) && labelsObj[key] !== undefined) {
    return labelsObj[key];
  }
  return key;
};

export {
  getLabelByNestedKeys,
  setLabelsData,
  getAllLabels,
  getLabelFromCopyOrFromTranslation,
  getDynamicLabelValues,
  renderLabel,
  getLabelWithDefaultValue,
};
